import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  Modal,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

function BoardingScreen() {
  const [screens, setScreen] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const roles =[
           {name : "driver"},
           {name : "customer"}
         ];

  const [screenData, setScreenData] = useState({
    heading1_en: "",
    heading1_fr: "",
    heading2_en: "",
    heading2_fr: "",
    description_en: "",
    description_fr: "",
    image_en: null,
    image_fr: null,
    role : "",
    status: "active",
  });

  const initialState = {
    heading1_en: "",
    heading1_fr: "",
    heading2_en: "",
    heading2_fr: "",
    description_en: "",
    description_fr: "",
    image_en: null,
    image_fr: null,
    role : "",
    status: "active",
  };

  // Fetch category with pagination
  const getScreens = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems };
        const headers = {
        };
        const response = await apiRequest.get(endPoints.GET_ONBOARDING,
          params,
          headers,
        );

        if (response.success) {
          const { items, pagination } = response.data;
          setScreen(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching category:", error);
        setError("Failed to fetch category. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [totalItems]
  );

  // Fetch category when currentPage changes
  useEffect(() => {
    getScreens(currentPage);
  }, [currentPage, getScreens]);

  // Handle modal open/close
  const handleOpenModal = () => {
    setOpenModal(true);
    setScreenData(initialState);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setScreenData(initialState);
    setIsEditing(false);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setScreenData({ ...screenData, [name]: value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      // Append text fields
      formData.append('heading1_en', screenData.heading1_en);
      formData.append('heading1_fr', screenData.heading1_fr);
      formData.append('heading2_en', screenData.heading2_en);
      formData.append('heading2_fr', screenData.heading2_fr);
      formData.append('description_en', screenData.description_en);
      formData.append('description_fr', screenData.description_fr);
      formData.append('user_type', screenData.role);

      // Append image files
      if (screenData.image_en instanceof File) {
        formData.append('image_en', screenData.image_en);
      }
      if (screenData.image_fr instanceof File) {
        formData.append('image_fr', screenData.image_fr);
      }
      // Determine the endpoint and method based on whether it's an edit or add operation
      const endPoint = isEditing
        ? `${endPoints.UPDATE_ONBOARDING}/${currentId}`
        : `${endPoints.ADD_ONBOARDING}`;
      const requestMethod = isEditing ? apiRequest.put : apiRequest.post;

      // Send the request
      const response = await requestMethod(endPoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Handle the response
      if (response.success) {
        setSuccess(isEditing ? "Screen updated successfully." : "Screen added successfully.");
        getScreens(currentPage); // Refresh the Category list
        handleCloseModal(); // Close the modal
      } else {
        setError(response.message || 'Failed to add/update Screen.');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while processing your request.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit servict type
  const handleEdit = (screen) => {
    handleOpenModal();
    setIsEditing(true);
    setScreenData({
      heading1_en: screen.heading1_en || "",
      heading1_fr: screen.heading1_fr || "",
      heading2_en: screen.heading2_en || "",
      heading2_fr: screen.heading2_fr || "",
      description_en: screen.description_en || "",
      description_fr: screen.description_fr || "",
      image_en: screen.image_en || null,
      image_en_preview: screen.image_en ? `${apiRequest.BASE_URL}/${screen.image_en}` : null,
      image_fr: screen.image_fr || null,
      image_fr_preview: screen.image_fr ? `${apiRequest.BASE_URL}/${screen.image_fr}` : null,
      status: screen.status || "active",
      role : screen.user_type
    });

    setCurrentId(screen.id);
  };

  // Handle delete screen
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Send the DELETE request to the API
      const response = await apiRequest.delete(
        `${endPoints.DELETE_ONBOARDING}/${id}`,
        {
          headers: {
          },
        }
      );

      // Handle the response
      if (response.success) {
        setSuccess("Screen deleted successfully.");

        // Update the local state to remove the deleted Category
        setScreen((prev) => prev.filter((screenData) => screenData.id !== id));
      } else {
        setError(response.message || "Failed to delete the screen.");
      }
    } catch (error) {
      console.error("Error deleting screen:", error);
      setError("An error occurred while deleting the screen.");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage); // Update the current page
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Update the state with the file and its preview
      setScreenData((prevState) => ({
        ...prevState,
        [fieldName]: file, // Store the file object
        [`${fieldName}_preview`]: URL.createObjectURL(file), // Generate a preview URL
      }));
    } 
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const payload = {
        status: newStatus,
      };

      const headers = {
        'Content-Type': 'application/json',
      };

      // Send the PUT request to update the Category status
      const response = await apiRequest.put(
        `${endPoints.STATUS_ONBOARDING}/${id}`,
        payload,
        { headers }
      );

      // Handle the response
      if (response.success) {
        setSuccess("Screen status updated successfully.");

        // Update the local state immediately
        setScreen((prev) =>
          prev.map((screenData) =>
            screenData.id === id ? { ...screenData, status: newStatus } : screenData
          )
        );
      } else {
        setError(response.message || 'Failed to update service type status.');
      } 
    } catch (error) {
      console.error('Error updating service type status:', error);
      setError('An error occurred while updating the service type status.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Boarding Screen
                </MDTypography>
              </MDBox>
              <MDBox pt={1} px={1}>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={2}
                >
                  <TextField
                    label="Search"
                    variant="outlined"
                    margin="normal"
                    placeholder="Search by title or description"
                    style={{ width: "300px" }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    style={{
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    Add Screen
                  </Button>
                </MDBox>
                <TableContainer component={Paper} style={{ marginTop: "1px" }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell style={{ fontWeight: "bold" }}>Heading</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Sub Heading</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Image</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Role</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
                      </TableRow>
                      {screens.map((screen) => (
                        <TableRow key={screen.id}>
                          <TableCell>{screen.heading1_en + ' / ' + screen.heading1_fr}</TableCell>
                          <TableCell>{screen.heading2_en + ' / ' + screen.heading2_fr}</TableCell>
                          <TableCell>
                            <img
                              src={`${apiRequest.BASE_URL}/${screen.image_en}`}
                              alt={screen.heading1_en}
                              style={{ width: "100px", height: "auto" }}
                            />/
                            <img
                              src={`${apiRequest.BASE_URL}/${screen.image_fr}`}
                              alt={screen.heading1_fr}
                              style={{ width: "100px", height: "auto" }}
                            />
                          </TableCell>
                          <TableCell>{screen.user_type }</TableCell>
                          <TableCell>
                            <Switch
                              checked={screen.status === "active"} // Assuming "active" is the active status
                              onChange={() => handleStatusToggle(screen.id, screen.status)}
                              color="primary"
                            />
                          </TableCell>

                          <TableCell>
                            <Button
                              onClick={() => handleEdit(screen)}
                              color="primary"
                              size="small"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => {
                                setCurrentId(screen.id); // Set the service type ID to delete
                                setDeleteDialogOpen(true); // Open the confirmation dialog
                              }}
                              color="error"
                              size="small"
                              style={{ marginLeft: "8px" }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages} {/* Display current page and total pages */}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal for Adding/Editing Service Type */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <MDBox
          p={3}
          bgcolor="white"
          borderRadius="lg"
          style={{
            width: 900,
            margin: "50px auto",
            outline: "none",
            backgroundColor: "white",
            maxHeight: "80vh", // Limit the height of the modal
            overflowY: "auto", // Enable vertical scrolling
          }}
        >
          <MDTypography variant="h6" mb={2}>
            {isEditing ? "Edit Screen" : "Add Screen"}
          </MDTypography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>

              <Grid item xs={12} sm={12}>
                <Autocomplete
                    options={roles}
                    getOptionLabel={(option) => option?.name || ""} 
                    value={screenData.role ? roles.find(role => role.name === screenData.role) : null}
                    onChange={(event, newValue) => {
                        setScreenData({ ...screenData, role: newValue ? newValue.name : null });
                    }}
                    renderInput={(params) => <TextField {...params} label="Select Role" />}
                    required
                />
            </Grid>

              {/* Title (EN) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Heading (EN)"
                  name="heading1_en"
                  value={screenData.heading1_en}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Grid>

              {/* Title (FR) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Heading (FR)"
                  name="heading1_fr"
                  value={screenData.heading1_fr}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sub Heading (EN)"
                  name="heading2_en"
                  value={screenData.heading2_en}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Grid>

              {/* Title (FR) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sub Heading (FR)"
                  name="heading2_fr"
                  value={screenData.heading2_fr}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Grid>

              {/* Description (EN) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description (EN)"
                  name="description_en"
                  value={screenData.description_en}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Description (FR) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description (FR)"
                  name="description_fr"
                  value={screenData.description_fr}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="file"
                    id="file-input-en"
                    onChange={(e) => handleFileChange(e, 'image_en')}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="file-input-en">
                    <Button variant="outlined" component="span" style={{ width: '100%', color: 'gray' }}>
                      Upload Image (EN)
                    </Button>
                  </label>
                  {screenData.image_en_preview && (
                    <img
                      src={screenData.image_en_preview}
                      alt="Preview"
                      style={{ width: '100px', height: 'auto', marginLeft: '10px' }}
                    />
                  )}
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="file"
                    id="file-input-ar"
                    onChange={(e) => handleFileChange(e, 'image_fr')}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="file-input-ar">
                    <Button variant="outlined" component="span" style={{ width: '100%', color: 'gray' }}>
                      Upload Image (FR)
                    </Button>
                  </label>
                  {screenData.image_fr_preview && (
                    <img
                      src={screenData.image_fr_preview}
                      alt="Preview"
                      style={{ width: '100px', height: 'auto', marginLeft: '10px' }}
                    />
                  )}
                </div>
              </Grid>

            </Grid>

            <MDBox mt={3} display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                style={{
                  whiteSpace: "nowrap",
                  color: "white",
                }}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </MDBox>
          </form>
        </MDBox>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Screen</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this screen? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false); // Close the dialog
              handleDelete(currentId); // Proceed with deletion
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // Positioning the Snackbar
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default BoardingScreen;